"use client";

import Link from "next/link";
import { useRouter } from "next/router";
import { Fragment, useEffect, useMemo, useState, type ReactNode } from "react";
import { useSession } from "@/components/providers/session";
import { currency } from "@/utils/formats";
import {
  buildPaymentPlanDetail,
  calculatePaymentPlanDeposit,
  getCourseListingPriceIncVatPence,
  getPaymentPlanPeriodOptions,
  IN_HOUSE_PAYMENT_PLAN_CARD_ANCHOR_ENTRY_IDS,
  paymentPlanCoursesFromEventDetail,
  shouldShowInHousePaymentPlanCard,
} from "@/utils/payment-plan";
import { isOnsitePriceExVatDisplay } from "@/utils/onsite-vat-display";
import {
  buildAdvisorCallbackPath,
  courseInterestFromPageCourse,
  recordCourseInterest,
} from "@/utils/course-interest";

type CourseEvent = Parameters<typeof shouldShowInHousePaymentPlanCard>[0] & {
  id?: string;
  contentful_id?: string;
  package?: boolean;
  events?: Array<{
    detail_id?: string;
    registrations?: unknown[];
    seats?: number;
    seat_price?: number;
    price?: number;
  }>;
};

function getNextAvailableEvent(event?: CourseEvent) {
  return event?.events?.find(
    (instance) => (instance.registrations?.length ?? 0) < (instance.seats ?? 0),
  );
}

async function fetchCourseForPaymentPlan(event?: CourseEvent) {
  if (!event?.id) {
    return null;
  }

  const response = await fetch(`/api/course-search/?id=${event.id}`, {
    method: "GET",
    headers: { "Content-Type": "application/json" },
  });

  if (!response.ok) {
    throw new Error("Could not load course for checkout");
  }

  return response.json();
}

function TermButton({
  months,
  selected,
  monthlyAmount,
  onSelect,
}: {
  months: number;
  selected: boolean;
  monthlyAmount: number;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={`min-w-[7.5rem] flex-1 rounded-lg border px-3 py-2.5 text-left transition-colors sm:flex-none ${
        selected
          ? "border-brand-500 bg-brand-500 text-white shadow-sm"
          : "border-gray-200 bg-white text-gray-900 hover:border-brand-300"
      }`}
    >
      <span className="block text-sm font-semibold">{months} months</span>
      <span
        className={`mt-0.5 block text-xs tabular-nums ${
          selected ? "text-white/90" : "text-gray-500"
        }`}
      >
        {months} x {currency(monthlyAmount)}
      </span>
    </button>
  );
}

function BreakdownRow({
  label,
  value,
  valueClassName = "text-gray-900",
}: {
  label: string;
  value: string;
  valueClassName?: string;
}) {
  return (
    <div className="flex items-center justify-between gap-4 py-3">
      <p className="text-sm text-gray-600">{label}</p>
      <p className={`text-base font-semibold tabular-nums sm:text-lg ${valueClassName}`}>
        {value}
      </p>
    </div>
  );
}

export default function InHousePaymentPlanCard({
  event,
  primary,
}: {
  event?: CourseEvent;
  primary?: Record<string, unknown>;
}) {
  const session = useSession();
  const router = useRouter();
  const [isEnrolling, setIsEnrolling] = useState(false);
  const [enrolError, setEnrolError] = useState<string | null>(null);
  const fullTotalPence = getCourseListingPriceIncVatPence(event) ?? 0;
  const trainingTotalLabel = isOnsitePriceExVatDisplay()
    ? "Training total (incl. VAT)"
    : "Training total";
  const courses = useMemo(() => paymentPlanCoursesFromEventDetail(event), [event]);
  const periods = useMemo(() => getPaymentPlanPeriodOptions(courses), [courses]);
  const [selectedTerm, setSelectedTerm] = useState(periods[0] ?? 3);
  const instalmentTermLabel =
    periods.includes(9) ? "3, 6 or 9 monthly payments" : "3 or 6 monthly payments";

  const courseInterest = courseInterestFromPageCourse({
    event,
    entry: primary as {
      sys?: { id?: string };
      fields?: { title?: string; url?: string; type?: string };
    },
    source: "advisor_cta",
  });
  const advisorCallbackHref = courseInterest
    ? buildAdvisorCallbackPath(courseInterest, "course_page")
    : "/request-a-callback/";

  const rememberCourseInterest = () => {
    if (courseInterest) {
      recordCourseInterest({
        ...courseInterest,
        source: "advisor_cta",
        captured_at: new Date().toISOString(),
      });
    }
  };

  useEffect(() => {
    if (!periods.includes(selectedTerm)) {
      setSelectedTerm(periods[0] ?? 3);
    }
  }, [periods, selectedTerm]);

  const deposit = calculatePaymentPlanDeposit(fullTotalPence);
  const plan = buildPaymentPlanDetail({
    fullTotalPence,
    numberOfPayments: selectedTerm,
  });

  const handleEnrolWithPaymentPlan = async () => {
    setIsEnrolling(true);
    setEnrolError(null);

    try {
      const coursePayload = primary
        ? {
            ...primary,
            event,
            package: event?.package,
            selectedEvent: getNextAvailableEvent(event) ?? event?.events?.[0] ?? null,
          }
        : await fetchCourseForPaymentPlan(event);

      if (!coursePayload?.fields && !coursePayload?.event) {
        throw new Error("Could not prepare this course for checkout");
      }

      session.addCourse({
        ...coursePayload,
        selectedEvent:
          coursePayload.selectedEvent ??
          getNextAvailableEvent(coursePayload.event ?? event) ??
          coursePayload.event?.events?.[0] ??
          null,
      });

      await router.push(
        `/checkout?payment_plan=1&payment_plan_term=${selectedTerm}`,
      );
    } catch {
      setEnrolError(
        "We couldn't add this course to checkout. Please try again or speak to admissions.",
      );
    } finally {
      setIsEnrolling(false);
    }
  };

  return (
    <div className="flex flex-col overflow-hidden rounded-lg bg-white shadow-lg lg:grid lg:grid-cols-6">
      <div className="bg-white px-6 py-6 lg:col-span-4 lg:p-8">
        <p className="text-xs font-semibold uppercase tracking-wide text-brand-600">
          In-house payment plan
        </p>
        <h5 className="mt-2 text-lg font-semibold text-gray-900 sm:text-xl">
          Spread the cost directly with us - no third-party finance, no credit
          check.
        </h5>
        <p className="mt-2 text-sm leading-6 text-gray-500">
          Pay 30% upfront plus a £250 admin fee to secure your place, then
          split the balance across {instalmentTermLabel} at 0%.
        </p>

        {fullTotalPence > 0 ? (
          <div className="mt-6 rounded-xl border border-brand-200 bg-white p-4 sm:p-5">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-sm font-medium text-gray-900">Choose your term</p>
              <div className="flex flex-wrap gap-2">
                {periods.map((months) => {
                  const preview = buildPaymentPlanDetail({
                    fullTotalPence,
                    numberOfPayments: months,
                  });

                  return (
                    <TermButton
                      key={months}
                      months={months}
                      selected={selectedTerm === months}
                      monthlyAmount={preview.monthlyAmount}
                      onSelect={() => setSelectedTerm(months)}
                    />
                  );
                })}
              </div>
            </div>

            <div className="mt-4 divide-y divide-gray-100 border-t border-gray-100">
              <BreakdownRow
                label={trainingTotalLabel}
                value={currency(fullTotalPence) ?? ""}
              />
              <BreakdownRow
                label="Course deposit (30%)"
                value={currency(plan.courseDepositAmount) ?? ""}
              />
              <BreakdownRow
                label="Admin fee (charged today)"
                value={currency(plan.adminFeeAmount) ?? ""}
              />
              <BreakdownRow
                label="Total due today"
                value={currency(deposit) ?? ""}
                valueClassName="text-brand-600"
              />
              <BreakdownRow
                label="Then monthly payments"
                value={currency(plan.monthlyAmount) ?? ""}
              />
            </div>

            <p className="mt-4 rounded-lg bg-brand-50 px-3 py-2.5 text-xs leading-5 text-gray-600">
              Pay 30% plus the £250 admin fee today to secure your place. Our
              team will confirm your instalment start date; monthly invoices are
              sent after that.
            </p>
          </div>
        ) : null}
      </div>
      <div className="flex flex-col justify-center bg-gray-50 px-6 py-6 text-center lg:col-span-2 lg:p-8">
        <p className="text-sm font-semibold text-gray-900">Total due today</p>
        <div className="mt-1 flex flex-col items-center text-3xl font-bold tabular-nums text-gray-900">
          <span>{currency(deposit)}</span>
          <span className="mt-0.5 text-xs font-normal text-gray-500">
            incl. VAT · includes £250 admin fee
          </span>
        </div>
        {fullTotalPence > 0 ? (
          <p className="mt-2 text-xs text-gray-600">
            Then {selectedTerm} x {currency(plan.monthlyAmount)} / month
          </p>
        ) : (
          <p className="mt-2 text-xs leading-5 text-gray-600">
            Pay 30% upfront to secure your place, then spread the balance across{" "}
            {instalmentTermLabel} at 0%.
          </p>
        )}
        <div className="mt-5 space-y-3">
          <button
            type="button"
            onClick={handleEnrolWithPaymentPlan}
            disabled={isEnrolling || fullTotalPence <= 0}
            className="inline-flex w-full items-center justify-center rounded-md bg-brand-600 px-5 py-3 text-sm font-semibold text-white shadow-sm transition-colors duration-200 hover:bg-brand-500 disabled:cursor-not-allowed disabled:opacity-70"
          >
            {isEnrolling ? "Adding to checkout..." : "Enrol with payment plan"}
          </button>
          <Link
            href={advisorCallbackHref}
            onClick={rememberCourseInterest}
            className="inline-flex w-full items-center justify-center rounded-md border border-gray-300 bg-white px-5 py-3 text-sm font-semibold text-gray-900 shadow-sm transition-colors duration-200 hover:border-brand-300 hover:text-brand-700"
          >
            Speak to admissions
          </Link>
        </div>
        {enrolError ? (
          <p className="mt-3 text-xs leading-5 text-red-600">{enrolError}</p>
        ) : null}
        <p className="mt-3 text-xs text-gray-600">
          Questions?{" "}
          <Link
            href={advisorCallbackHref}
            onClick={rememberCourseInterest}
            className="font-medium text-gray-900 underline"
          >
            Contact us
          </Link>
        </p>
      </div>
    </div>
  );
}

type InHousePaymentPlanInsertLayout = "section" | "grid";

type InHousePaymentPlanInsertProps = {
  entryId?: string;
  event?: CourseEvent;
  primary?: Record<string, unknown>;
  courseType?: string;
  layout?: InHousePaymentPlanInsertLayout;
  children: ReactNode;
};

function PaymentPlanShell({
  layout,
  children,
}: {
  layout: InHousePaymentPlanInsertLayout;
  children: ReactNode;
}) {
  if (layout === "grid") {
    return <div className="col-span-full mt-6 sm:mt-8">{children}</div>;
  }

  return (
    <section className="bg-gray-50 py-10 sm:py-14">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">{children}</div>
    </section>
  );
}

export function InHousePaymentPlanInsert({
  entryId,
  event,
  primary,
  courseType,
  layout = "section",
  children,
}: InHousePaymentPlanInsertProps) {
  const showCard =
    courseType === "Course" &&
    IN_HOUSE_PAYMENT_PLAN_CARD_ANCHOR_ENTRY_IDS.includes(
      entryId as (typeof IN_HOUSE_PAYMENT_PLAN_CARD_ANCHOR_ENTRY_IDS)[number],
    ) &&
    shouldShowInHousePaymentPlanCard(event);

  return (
    <Fragment>
      {children}
      {showCard ? (
        <PaymentPlanShell layout={layout}>
          <InHousePaymentPlanCard event={event} primary={primary} />
        </PaymentPlanShell>
      ) : null}
    </Fragment>
  );
}
